<script setup>
import SiteLayout from '@/Layouts/Layout.vue';
import Card from '@/Components/Card/Card.vue';
import { computed, reactive, watch } from 'vue';
import TextInput from '@/Components/Input/TextInput.vue';
import Alert from '@/Components/Alert.vue';
import PrimaryButton from '@/Components/Button/PrimaryButton.vue';
import Select from '@/Components/Select.vue';
import CopyButton from '@/Components/Button/CopyButton.vue';
import isValidDkimSelector from '@/Common/IsValidDkimSelector.js';
import { TOOL_DKIM_GENERATOR, captureToolUsed } from '@/Common/Posthog.js';

const state = reactive({
  domain: '',
  dkimSelector: '',
  keySize: 1024,
  data: null,
  isDkimSelectorValid: true
});

const cname = computed(
  () => `${state.dkimSelector}._domainkey.${state.domain}`
);

watch(
  () => state.dkimSelector,
  (newValue) => {
    state.isDkimSelectorValid = isValidDkimSelector(newValue);
  }
);

const dkimRecord = computed(() => {
  let publicKey = state.data.public
    .replace('-----BEGIN PUBLIC KEY-----', '')
    .replace('-----END PUBLIC KEY-----', '')
    .replace(/\s/g, '');
  return `v=DKIM1;t=s;p=${publicKey}`;
});

async function generateDkimKeys() {
  if (!state.isDkimSelectorValid) {
    return;
  }
  state.data = null;

  const keyPair = await crypto.subtle.generateKey(
    {
      name: 'RSA-OAEP',
      modulusLength: state.keySize,
      publicExponent: new Uint8Array([1, 0, 1]),
      hash: 'SHA-256'
    },
    true,
    ['encrypt', 'decrypt']
  );

  const publicKey = await crypto.subtle.exportKey('spki', keyPair.publicKey);
  const publicKeyPem = convertArrayBufferToPem(publicKey, 'PUBLIC KEY');

  const privateKey = await crypto.subtle.exportKey('pkcs8', keyPair.privateKey);
  const privateKeyPem = convertArrayBufferToPem(privateKey, 'PRIVATE KEY');

  state.data = {
    private: privateKeyPem,
    public: publicKeyPem
  };

  captureToolUsed({
    tool: TOOL_DKIM_GENERATOR,
    properties: {
      tested_domain: state.domain,
      key_size: Number(state.keySize)
    }
  });
}

function convertArrayBufferToPem(buffer, type, lineLength = 64) {
  const base64String = btoa(String.fromCharCode(...new Uint8Array(buffer)));

  const chunkedBase64 = base64String
    .match(new RegExp(`.{1,${lineLength}}`, 'g'))
    .join('\n');

  return `-----BEGIN ${type}-----\n${chunkedBase64}\n-----END ${type}-----\n`;
}
</script>

<template>
  <SiteLayout
    :title="$t('dkim_generator.index.dkim_key_generator')"
    :description="$t('dkim_generator.index.dkim_key_generator_description')"
  >
    <Card>
      <form @submit.prevent="generateDkimKeys">
        <div class="flex flex-col justify-between gap-4 md:flex-row">
          <TextInput
            v-model.trim="state.domain"
            size="large"
            :label="$t('dkim_generator.index.dkim_domain')"
            :description="$t('dkim_generator.index.domain_example')"
            clearable
            required
          />

          <TextInput
            v-model.trim="state.dkimSelector"
            size="large"
            :variant="state.isDkimSelectorValid ? 'default' : 'danger'"
            :label="$t('dkim_generator.index.dkim_selector')"
            :description="
              state.isDkimSelectorValid
                ? 'e.g. myselector'
                : $t('dkim_generator.index.dkim_selector_not_valid')
            "
            clearable
            required
          />
          <Select
            v-model="state.keySize"
            size="large"
            :label="$t('dkim_generator.index.key_size')"
          >
            <option>1024</option>
            <option>2048</option>
          </Select>

          <div class="flex md:items-center md:justify-center">
            <PrimaryButton
              size="large"
              :is-loading="state.isLoading"
              class="mt-2"
            >
              {{ $t('dkim_generator.index.generate_dkim_record') }}
            </PrimaryButton>
          </div>
        </div>
      </form>
    </Card>

    <Card v-if="state.data">
      <div class="flex flex-col gap-6">
        <Alert
          v-if="state.data"
          :title="$t('dkim_generator.index.dkim_record_generated')"
          variant="success"
        >
          {{ $t('dkim_generator.index.please_store_this_information') }}
        </Alert>

        <div>
          <div class="font-bold">
            {{ $t('dkim_generator.index.private_key') }}
          </div>
          <div class="mb-2">
            {{ $t('dkim_generator.index.enter_this_key_into') }}
          </div>
          <div
            class="bg-code text-code-content relative rounded-xl p-6 text-sm"
          >
            <CopyButton
              class="absolute top-6 right-6"
              :copy-text="state.data.private"
            />
            {{ state.data.private }}
          </div>
        </div>

        <div>
          <div class="font-bold">
            {{ $t('dkim_generator.index.dkim_record') }}
          </div>
          <div class="mb-2">
            {{
              $t('dkim_generator.index.dkim_record_description', {
                cname: cname
              })
            }}
          </div>
          <div
            class="bg-code text-code-content relative overflow-auto rounded-xl p-6 text-sm break-words"
          >
            <CopyButton
              class="absolute top-6 right-6"
              :copy-text="dkimRecord"
            />
            {{ dkimRecord }}
          </div>
        </div>

        <div>
          <div class="font-bold">
            {{ $t('dkim_generator.index.public_key') }}
          </div>
          <div class="mb-2">
            {{ $t('dkim_generator.index.this_is_the_public') }}
          </div>
          <div
            class="bg-code text-code-content relative rounded-xl p-6 text-sm"
          >
            <CopyButton
              class="absolute top-6 right-6"
              :copy-text="state.data.public"
            />
            {{ state.data.public }}
          </div>
        </div>
      </div>
    </Card>
  </SiteLayout>
</template>
